import { PushpinFilled, PushpinOutlined } from '@ant-design/icons'
import React, { useCallback, useMemo } from 'react'
import { Link, useNavigate } from 'react-router'
import styled from 'styled-components'
import { Button } from 'sutra/components/Button'
import { Popover } from 'sutra/components/Popover/Popover'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'

import { cardBorder, darkBlueFontColor } from 'assets/styles/variables'
import {
  ElevationLevelPopover,
  type TierConfig,
} from 'features/trend-detection/components/ElevationLevelPopover'
import { MiniTimeseries } from 'features/trend-detection/components/MiniTimeseries'
import { TREND_DETECTION_PRIMARY_BLUE } from 'features/trend-detection/constants'
import {
  ISSUE_TRACK_TOOLTIP,
  ISSUE_UNTRACK_TOOLTIP,
} from 'features/trend-detection/constants'
import {
  ISSUE_DETECTION_AGENT_TYPE,
  type IssueFeedTab,
  ResearchAgentsAction,
  trackResearchAgents,
} from 'features/trend-detection/mixpanel'
import {
  trendDetectionAgentConfigPath,
  trendDetectionIssueDetailPath,
} from 'features/trend-detection/paths'
import type {
  Chart,
  IssueCardData,
  IssueResponse,
} from 'features/trend-detection/types'
import {
  type EmergenceLevel,
  formatLocalDateFromISO,
} from 'features/trend-detection/utils/helpers'

type ActiveEmergenceLevel = Exclude<EmergenceLevel, 'nothing'>

import {
  EMERGENCE_BOX_BG,
  EMERGENCE_BOX_BORDER,
  EMERGENCE_COLORS,
  EMERGENCE_ICON_COLOR,
  EMERGENCE_NOTHING_COLOR,
} from 'features/trend-detection/components/issue-constants'
import {
  DetectedIssueIcon,
  EmergenceBarChart,
  EmergenceBarChartForLevel,
  SafetyIndicator,
} from 'features/trend-detection/components/IssueShared'

const EMERGENCE_TIERS: TierConfig<ActiveEmergenceLevel>[] = (
  [
    ['slightly_elevated', 'Slightly elevated'],
    ['elevated', 'Elevated'],
    ['sharply_elevated', 'Sharply elevated'],
  ] as const
).map(([key, label]) => ({
  key,
  label,
  color: EMERGENCE_COLORS[key],
  icon: (active: boolean) => (
    <EmergenceBarChartForLevel level={key} active={active} />
  ),
}))

export interface IssueCardProps {
  issueCard: IssueCardData
  issue: IssueResponse | undefined
  issueIndex: number
  emergenceLevel: EmergenceLevel
  timeseriesChart: Chart | undefined
  chartLoading: boolean
  projectTotalChart?: Chart | null
  agentUuid?: string | null
  agentName?: string | null
  agentType?: string
  runUuid?: string | null
  isShared: boolean
  projectId: string | null
  onIssueTrackingChange?: (nextTracked: boolean) => void
  trackingLoading?: boolean
  feedTab?: IssueFeedTab | null
  onAgentNameNavigate?: () => void
  source?: 'emerging_issues' | 'key_insights'
}

export const IssueCard: React.FC<IssueCardProps> = ({
  issueCard,
  issue,
  issueIndex,
  emergenceLevel,
  timeseriesChart,
  chartLoading,
  projectTotalChart,
  isShared,
  projectId,
  agentUuid,
  agentName,
  agentType,
  runUuid,
  onIssueTrackingChange,
  trackingLoading = false,
  feedTab = null,
  onAgentNameNavigate,
  source,
}) => {
  const feedTabProps = useMemo(
    () => (feedTab ? { feed_tab: feedTab } : {}),
    [feedTab],
  )
  const resolvedAgentType = agentType ?? ISSUE_DETECTION_AGENT_TYPE
  const emergenceMultiplier =
    issueCard.emergence_score_normalized === null
      ? Infinity
      : typeof issueCard.emergence_score_normalized === 'number'
        ? issueCard.emergence_score_normalized
        : null

  const currentShare =
    issueCard.emergence_score_normalized != null &&
    issueCard.prior_period_share != null
      ? issueCard.emergence_score_normalized * issueCard.prior_period_share
      : null

  const isClickable = !isShared && !!issue && !!projectId

  const navigate = useNavigate()
  const handleCardClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isClickable) return
      if ((e.target as HTMLElement).closest('button, a')) return
      trackResearchAgents(ResearchAgentsAction.issue_card_click, {
        issue_uuid: issue!.uuid,
        issue_title: issue!.title,
        is_severe: issue!.is_severe,
        agent_type: resolvedAgentType,
        agent_uuid: agentUuid,
        agent_name: agentName ?? null,
        run_uuid: runUuid,
        ...feedTabProps,
        ...(source ? { source } : {}),
      })
      navigate(trendDetectionIssueDetailPath(projectId!, issue!.uuid))
    },
    [
      isClickable,
      navigate,
      projectId,
      issue,
      resolvedAgentType,
      agentUuid,
      agentName,
      runUuid,
      feedTabProps,
      source,
    ],
  )

  return (
    <IssueCardStyled
      $clickable={isClickable}
      onClick={handleCardClick}
      data-chameleon="issue-card"
    >
      <IssueCardTopRow>
        <IssueCardMainContentColumn>
          <IssueCardHeader>
            <DetectionStatusBadgeRow>
              <DetectionStatusIconBox $level={emergenceLevel}>
                <DetectedIssueIcon
                  color={EMERGENCE_ICON_COLOR[emergenceLevel]}
                  width={24}
                  height={24}
                />
              </DetectionStatusIconBox>
              <UpdateStatusBadgeLabels>
                <UpdateStatusBadgeTitle>Issue Detected</UpdateStatusBadgeTitle>
                <EmergenceBadgesWrap>
                  <EmergenceBarChart level={emergenceLevel} />
                  <EmergenceBadgeLabel
                    $color={
                      emergenceLevel !== 'nothing'
                        ? EMERGENCE_COLORS[emergenceLevel]
                        : EMERGENCE_NOTHING_COLOR
                    }
                  >
                    {emergenceLevel === 'nothing'
                      ? 'Not currently elevated'
                      : emergenceLevel
                          .replace(/_/g, ' ')
                          .replace(/^\w/, (c) => c.toUpperCase())}
                  </EmergenceBadgeLabel>
                  {emergenceLevel !== 'nothing' && (
                    <ElevationLevelPopover
                      tiers={EMERGENCE_TIERS}
                      activeTierKey={emergenceLevel}
                      share={currentShare}
                      comparisonMultiplier={emergenceMultiplier}
                    />
                  )}
                  {(issueCard.is_severe || issue?.is_severe) && (
                    <SafetyIndicator />
                  )}
                </EmergenceBadgesWrap>
              </UpdateStatusBadgeLabels>
            </DetectionStatusBadgeRow>
            <IssueCardTitleRow>
              <IssueCardTitle className="issue-card-title">
                {issueCard.title ||
                  `Issue ${issueCard.original_index ?? issueIndex + 1}`}
              </IssueCardTitle>
            </IssueCardTitleRow>
            {issueCard.description && (
              <IssueCardDescription>
                {issueCard.description}
              </IssueCardDescription>
            )}
          </IssueCardHeader>

          <IssueCardFooter>
            <FooterMeta>
              {projectId && agentUuid && agentName ? (
                <FooterAgentLink
                  to={trendDetectionAgentConfigPath(projectId, agentUuid)}
                  onClick={() => onAgentNameNavigate?.()}
                >
                  {agentName}
                </FooterAgentLink>
              ) : agentName ? (
                <FooterAgentText>{agentName}</FooterAgentText>
              ) : null}
              {issue && (
                <Tooltip content="Detection date">
                  <FooterTime>
                    · {formatLocalDateFromISO(issue.create_time)}
                  </FooterTime>
                </Tooltip>
              )}
            </FooterMeta>
            <IssueCardActionsRow>
              {!isShared &&
                issue &&
                !onIssueTrackingChange &&
                issue.is_tracked && (
                  <PassiveTrackingChip data-testid="passive-tracking-indicator">
                    <TrackingPinIconTracked>
                      <PushpinFilled />
                    </TrackingPinIconTracked>
                    <TrackingStatusText>Tracking</TrackingStatusText>
                  </PassiveTrackingChip>
                )}
              {!isShared &&
                issue &&
                onIssueTrackingChange &&
                (() => {
                  const isTracked = issue.is_tracked ?? false
                  return (
                    <Popover
                      content={
                        isTracked ? ISSUE_UNTRACK_TOOLTIP : ISSUE_TRACK_TOOLTIP
                      }
                      openOnHover
                      initialFocus={false}
                    >
                      <TrackingToggleButton
                        data-testid="issue-tracking-control"
                        variant="secondary"
                        size="xs"
                        loading={trackingLoading}
                        $tracked={isTracked}
                        onClick={() => {
                          trackResearchAgents(
                            isTracked
                              ? ResearchAgentsAction.issue_untrack_click
                              : ResearchAgentsAction.issue_track_click,
                            {
                              issue_uuid: issue.uuid,
                              issue_title: issue.title,
                              is_severe: issue.is_severe,
                              agent_type: resolvedAgentType,
                              agent_uuid: agentUuid,
                              agent_name: agentName ?? null,
                              run_uuid: runUuid,
                              source: source ?? 'agent_detail',
                              ...feedTabProps,
                            },
                          )
                          onIssueTrackingChange(!isTracked)
                        }}
                      >
                        {isTracked ? (
                          <TrackingPinIconTracked>
                            <PushpinFilled />
                          </TrackingPinIconTracked>
                        ) : (
                          <TrackingPinIcon>
                            <PushpinOutlined />
                          </TrackingPinIcon>
                        )}
                        <TrackingStatusText>
                          {isTracked ? 'Tracking' : 'Track'}
                        </TrackingStatusText>
                      </TrackingToggleButton>
                    </Popover>
                  )
                })()}
            </IssueCardActionsRow>
          </IssueCardFooter>
        </IssueCardMainContentColumn>
        <MiniTimeseries
          chart={timeseriesChart}
          loading={chartLoading}
          multiplier={emergenceMultiplier}
          recordCount={
            issueCard.total_document_count ?? issueCard.post_texts?.length ?? 0
          }
          projectTotalChart={projectTotalChart}
          baselineShare={issueCard.prior_period_share}
        />
      </IssueCardTopRow>
    </IssueCardStyled>
  )
}

const TrackingToggleButton = styled(Button)<{ $tracked: boolean }>`
  padding-inline: 10px;
  border-color: ${(p) =>
    p.$tracked ? 'rgba(79, 114, 164, 0.28)' : 'rgba(31, 52, 84, 0.16)'};
  border-radius: 999px;
  background: ${(p) =>
    p.$tracked ? 'rgba(79, 114, 164, 0.08)' : 'rgba(255, 255, 255, 0.92)'};

  &&:hover:not(:disabled):not([aria-disabled='true']),
  &&:focus-visible {
    border-color: rgba(79, 114, 164, 0.4);
    color: ${darkBlueFontColor};
    background: ${(p) =>
      p.$tracked ? 'rgba(79, 114, 164, 0.08)' : 'rgba(255, 255, 255, 0.92)'};
  }
`

const TrackingPinIcon = styled.span`
  display: inline-flex;
  align-items: center;
  opacity: 0.72;
`

const TrackingPinIconTracked = styled(TrackingPinIcon)`
  color: ${TREND_DETECTION_PRIMARY_BLUE};
`

const TrackingStatusText = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${darkBlueFontColor};
  opacity: 0.92;
`

// Passive (non-interactive) "Tracking" pill rendered on read-only feed
// surfaces where the user can't toggle tracking from the card itself —
// preserves the visual signal that the issue is currently tracked.
const PassiveTrackingChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 14px;
  background: rgba(24, 144, 255, 0.08);
  color: ${TREND_DETECTION_PRIMARY_BLUE};
  font-size: 12px;
  font-weight: 500;
`

const EmergenceBadgesWrap = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
`

const IssueCardStyled = styled.div<{
  $clickable?: boolean
}>`
  background: white;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid ${cardBorder};
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: relative;

  ${(p) =>
    p.$clickable &&
    `
    cursor: pointer;
    transition: box-shadow 0.2s, border-color 0.2s;

    &:hover {
      box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.12);
      border-color: ${TREND_DETECTION_PRIMARY_BLUE};
    }
  `}
`

const IssueCardTopRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 24px;
`

const IssueCardMainContentColumn = styled.div`
  flex: 1;
  min-width: 200px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 0;
`

const IssueCardHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const IssueCardTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const IssueCardTitle = styled.h3`
  font-family: Poppins, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0;
  color: ${darkBlueFontColor};
  margin: 0;
`

const IssueCardDescription = styled.p`
  font-family: Poppins, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;
  letter-spacing: 0;
  color: #868d98;
  margin: 0;
`

const IssueCardFooter = styled.div`
  margin-top: auto;
  padding-top: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  position: relative;
  z-index: 1;
`

const IssueCardActionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const DetectionStatusBadgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  position: relative;
  z-index: 1;
`

const DetectionStatusIconBox = styled.span<{ $level: EmergenceLevel }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 7px;
  flex-shrink: 0;
  border-radius: 5px;
  background: ${({ $level }) => EMERGENCE_BOX_BG[$level]};
  border: 0.86px solid ${({ $level }) => EMERGENCE_BOX_BORDER[$level]};
`

const UpdateStatusBadgeLabels = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`

const UpdateStatusBadgeTitle = styled.span`
  font-family: var(--Font-family-font-family-body, Poppins), serif;
  font-size: var(--Font-size-text-sm, 14px);
  font-weight: 600;
  line-height: var(--Line-height-text-sm, 20px);
  letter-spacing: 0;
  color: ${darkBlueFontColor};
`

const EmergenceBadgeLabel = styled.span<{ $color: string }>`
  font-family: var(--Font-family-font-family-body, Poppins), serif;
  font-size: var(--Font-size-text-xs, 12px);
  font-weight: 600;
  line-height: var(--Line-height-text-xs, 16px);
  letter-spacing: 0;
  color: ${(p) => p.$color};
`

const FooterMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  line-height: 1.4;
`

const FooterAgentLink = styled(Link)`
  color: ${TREND_DETECTION_PRIMARY_BLUE};
  font-weight: 500;
  text-decoration: none;
  position: relative;
  z-index: 1;

  &:hover {
    text-decoration: underline;
  }
`

const FooterAgentText = styled.span`
  color: ${TREND_DETECTION_PRIMARY_BLUE};
  font-weight: 500;
`

const FooterTime = styled.span`
  color: #666;
`
